import { useMemo } from 'react';
import { useLiveRxQuery } from 'rxdb/plugins/react';
import { useStableQuery } from '../../db/useStableQuery';
import type { FindingDocType } from '@ast-data/shared';
import { useFamilyProjectIds } from '../projects/useFamilyProjectIds';

/**
 * Deterministic, client-side detection of finding-number collisions within a
 * project's Teilmaßnahme family (linked projects share Befunde, so a number
 * must be unique across the family). Because every device replicates all
 * findings, two findings created offline with the same number become visible
 * once they sync — a cross-document conflict RxDB's per-id conflict handler
 * cannot see.
 *
 * Returns a map from each colliding finding's id to its (trimmed) number.
 * Unlike the project-duplicate rule there is no canonical/merge: ALL members
 * are flagged and the user renumbers one.
 */
export function computeFindingDuplicates(
    findings: FindingDocType[]
): Map<string, string> {
    const byNumber = new Map<string, FindingDocType[]>();
    for (const finding of findings) {
        const key = finding.findingNumber.trim().toLowerCase();
        if (key === '') {
            continue;
        }
        const group = byNumber.get(key);
        if (group) {
            group.push(finding);
        } else {
            byNumber.set(key, [finding]);
        }
    }

    const duplicates = new Map<string, string>();
    for (const group of byNumber.values()) {
        if (group.length < 2) {
            continue;
        }
        for (const finding of group) {
            duplicates.set(finding.id, finding.findingNumber.trim());
        }
    }
    return duplicates;
}

/** Map of colliding finding id -> its number, across the project's family. */
export function useFindingDuplicates(projectId: string): Map<string, string> {
    const familyIds = useFamilyProjectIds(projectId);
    const resultQuery = useStableQuery<FindingDocType>({
        selector: { projectId: { $in: familyIds } }
    });
    const { results: resultDocs } = useLiveRxQuery<FindingDocType>({
        collection: 'findings',
        query: resultQuery
    });
    const result = useMemo(
        () => resultDocs.map((doc) => doc.toMutableJSON() as FindingDocType),
        [resultDocs]
    );
    return useMemo(() => computeFindingDuplicates(result), [result]);
}
